import _ from "lodash";
import React, { CSSProperties } from "react";

import { CumulativeMetricObject } from "@/__generated__/graphql";
import {
  FieldDefinitionFieldType,
  FieldDefinitionType,
  FieldDefinitionTypeType,
} from "@/components/@types";
import { subDays } from "date-fns";
import { forwardRef } from "react";
import { NumericFormat } from "react-number-format";

import { Utils } from "@/utils";
import { typographyStyles } from "@/components/ui/typography";

export function useFieldVariantProps(
  fieldVariant: FieldDefinitionFieldType<FieldDefinitionType>["fieldVariant"],
): {
  color?: string;
  variant?: string;
  style?: React.CSSProperties;
} {
  if (fieldVariant === "cell") {
    return {
      variant: "outlined",
      color: "primary",
      style: {},
    };
  } else {
    return {
      variant: "plain",
      color: "neutral",
      style: {
        transition: "all 0.1s ease-in-out",
        outline: `1px solid transparent`,
      },
    };
  }
}

export const FormattedCurrencyInput = forwardRef<
  HTMLInputElement,
  {
    name: string;
    onChange: (e) => void;
    value?: string | number;
    disabled?: boolean;
    onBlur?: () => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    placeholder?: string;
    style?: React.CSSProperties;
    /** The currency it is in, where that is not its business's: its sign leads it. */
    currency?: string | null;
  }
>(function NumericFormatAdapter(props, ref) {
  const { onChange, style, currency, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values, sourceInfo) => {
        // Only a keystroke is an edit. ``NumericFormat`` also fires this when
        // the ``value`` prop changes, so a cell re-rendered from new state
        // would echo its own stale value straight back into the store — which,
        // where two views edit the same field (the invoice canvas and the line
        // items grid), overwrites the edit that caused the re-render.
        if (sourceInfo.source !== "event") {
          return;
        }
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      thousandSeparator
      valueIsNumericString
      prefix={Utils.fCurrency(0, false, false, currency).replace(
        /[0-9.,]/g,
        "",
      )}
      decimalScale={2}
      fixedDecimalScale
      style={{
        ...(typographyStyles["mono-body-md"] as React.CSSProperties),
        ...style,
      }}
    />
  );
});

export const Input = forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>((props, ref) => <input ref={ref} {...props} />);

export const NumberInput = forwardRef<
  HTMLInputElement,
  {
    name: string;
    onChange: (e) => void;
    value?: string | number;
    disabled?: boolean;
    onBlur?: () => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    placeholder?: string;
    style?: React.CSSProperties;
  }
>(function NumericFormatAdapter(props, ref) {
  const { onChange, style, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values, sourceInfo) => {
        // Only a keystroke is an edit. ``NumericFormat`` also fires this when
        // the ``value`` prop changes, so a cell re-rendered from new state
        // would echo its own stale value straight back into the store — which,
        // where two views edit the same field (the invoice canvas and the line
        // items grid), overwrites the edit that caused the re-render.
        if (sourceInfo.source !== "event") {
          return;
        }
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      thousandSeparator
      valueIsNumericString
      // decimalScale={2}
      style={{
        ...(typographyStyles["mono-body-md"] as React.CSSProperties),
        ...style,
      }}
    />
  );
});

export const PercentInput = forwardRef<
  HTMLInputElement,
  {
    name: string;
    onChange: (e) => void;
    value?: string | number;
    disabled?: boolean;
    onBlur?: () => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    placeholder?: string;
    style?: React.CSSProperties;
  }
>(function PercentInput(props, ref) {
  const { onChange, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values, sourceInfo) => {
        // Only a keystroke is an edit. ``NumericFormat`` also fires this when
        // the ``value`` prop changes, so a cell re-rendered from new state
        // would echo its own stale value straight back into the store — which,
        // where two views edit the same field (the invoice canvas and the line
        // items grid), overwrites the edit that caused the re-render.
        if (sourceInfo.source !== "event") {
          return;
        }
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      suffix="%"
    />
  );
});

/**
 *
 * @param name
 * @param cumulativeMetric
 * @returns
 */
function getFieldMetricValueFromCumulativeMetricInner(
  name: string,
  cumulativeMetric: CumulativeMetricObject,
) {
  const metric = cumulativeMetric;

  const value = metric?.endingValue;
  const previousValue = metric?.startingValue;

  const adjustedValue = value || 0;
  const adjustedPreviousValue = previousValue || 0;

  const delta = adjustedValue - adjustedPreviousValue;
  const valuesAreNull = value == null || previousValue == null;

  let unit = "ignore";
  if (!valuesAreNull && delta !== 0) {
    const calculatedValue = (delta / adjustedPreviousValue) * 100;
    if (
      Math.abs(calculatedValue) > 0.01 &&
      calculatedValue !== Infinity &&
      calculatedValue !== -Infinity
    ) {
      unit = "percent";
    } else {
      unit = "relative";
    }
  }
  return {
    metric: {
      startDate: metric?.startDate,
      endDate: metric?.endDate
        ? subDays(new Date(metric.endDate), 1).toISOString()
        : undefined,
      startingValue: adjustedPreviousValue,
      endingValue: adjustedValue,
      delta: delta,
    },
    unit,
    label: name,
    isPositive: delta > 0,
  };
}

export const getFieldMetricValueFromCumulativeMetric = _.memoize(
  getFieldMetricValueFromCumulativeMetricInner,
  (name, cumulativeMetric) =>
    `${name}-${cumulativeMetric?.startDate || ""}-${
      cumulativeMetric?.endDate || ""
    }`,
);

export const getDefaultFieldCellPositionStyles = (
  fieldType: FieldDefinitionTypeType,
  size: "sm" | "md" = "md",
) => {
  const styles = {
    address: {},
    boolean: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    button: {},
    currency: {
      color: "var(--foreground)",
      textAlign: "right",
      justifyContent: "flex-end",
      alignItems: "center",
    },
    custom: {},
    date: {
      display: "flex",
      textAlign: "right",
      justifyContent: "flex-end",
      alignItems: "center",
    },
    datetime: {},
    email: {},
    enum: {
      display: "flex",
      justifyContent: "flex-start",
    },
    enums: {
      display: "flex",
      justifyContent: "flex-start",
      overflow: "hidden",
      flexDirection: "row",
      alignItems: "center",
      textOverflow: "ellipsis",
      flexWrap: "nowrap",
    },
    image: {},
    list: {},
    metric: {
      display: "flex",
      textAlign: "right",
      justifyContent: "flex-end",
      alignItems: "center",
    },
    number: {
      textAlign: "right",
      justifyContent: "flex-end",
      alignItems: "center",
    },
    path: {
      display: "flex",
      justifyContent: "flex-start",
      alignItems: "center",
    },
    percent: {
      textAlign: "right",
      justifyContent: "flex-end",
      alignItems: "center",
    },
    phone: {},
    reference: {
      paddingRight: 0,
    },
    references: {
      paddingRight: 0,
    },
    router: {},
    string: {},
    table: {},
    tag: {},
    tags: {},
    text: {
      color: "var(--foreground)",
      textAlign: "left",
      justifyContent: "flex-start",
      alignItems: "center",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    time: {},
    url: {},
  };
  const cellStyle = styles[fieldType] as Record<
    string,
    CSSProperties | string | number
  >;

  return cellStyle;
};
